import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RankedListWidget } from '../../dashboard-basic-view/components/RankedListWidget/ranked-list-widget';
import { NoDataWidget } from '../../dashboard-basic-view/components/NoDataWidget/no-data-widget';
import { formatNumber, formatPercent } from '../../../reporting/components/portfolio-report';
import { ReportingPaths } from '../../../reporting/reporting.router';
import { PortfolioWidgetProps } from './portfolio-widget.types';

export function PortfolioTotalRentalIncomeWidget({ data, isLoading }: PortfolioWidgetProps) {
  const { t } = useTranslation('widget');
  const currencyCode = data?.currency ?? 'NOK';

  const { total, totalBudget, mostOverBudget, mostUnderBudget } = useMemo(() => {
    const buildings = data?.buildings ?? [];

    // Headline: total actual rental income across the portfolio, plus the total budget.
    const total = buildings
      .filter((b) => b.financialRentalIncome != null)
      .reduce((sum, b) => sum + (b.financialRentalIncome as number), 0);
    const totalBudget = buildings
      .filter((b) => b.budgetRentalIncome != null)
      .reduce((sum, b) => sum + (b.budgetRentalIncome as number), 0);

    // Lists: rank buildings by rental income deviation vs. budget (in percent).
    const variances = buildings
      .filter((b) => b.financialRentalIncome != null && b.budgetRentalIncome != null && b.budgetRentalIncome !== 0)
      .map((b) => {
        const actual = b.financialRentalIncome as number;
        const budget = b.budgetRentalIncome as number;
        return { id: b.id, label: b.name, value: Math.round(((actual - budget) / budget) * 1000) / 10 };
      });

    return {
      total,
      totalBudget,
      mostOverBudget: variances
        .filter((v) => v.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 3),
      mostUnderBudget: variances
        .filter((v) => v.value < 0)
        .sort((a, b) => a.value - b.value)
        .slice(0, 3),
    };
  }, [data]);

  const totalVariancePercent = totalBudget !== 0 ? ((total - totalBudget) / totalBudget) * 100 : null;
  const budgetSubtitle = `${t('portfolioTotalRentalIncome.budgetLabel')}: ${formatNumber(totalBudget / 1000)} k${currencyCode}${
    totalVariancePercent != null
      ? ` (${totalVariancePercent > 0 ? '+' : ''}${formatPercent(totalVariancePercent)})`
      : ''
  }`;

  if (!isLoading && mostOverBudget.length === 0 && mostUnderBudget.length === 0)
    return (
      <NoDataWidget
        title={t('portfolioTotalRentalIncome.title')}
        noDataText={t('portfolioTotalRentalIncome.noData')}
      />
    );

  return (
    <RankedListWidget
      to={ReportingPaths.FinancialOverview}
      title={t('portfolioTotalRentalIncome.title')}
      tooltip={t('portfolioTotalRentalIncome.tooltip')}
      value={formatNumber(total / 1000)}
      unit={`k${currencyCode}`}
      valueSubtitle={budgetSubtitle}
      rankUnit="%"
      loading={isLoading || !data}
      highListTitle={t('portfolioTotalRentalIncome.mostOverBudget')}
      lowListTitle={t('portfolioTotalRentalIncome.mostUnderBudget')}
      highListData={mostOverBudget}
      lowListData={mostUnderBudget}
      emptyText={t('portfolioTotalRentalIncome.notAvailable')}
    />
  );
}
